import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import "../pages/css/List.css";

const COL_WIDTH_STORAGE_PREFIX = "datatable-col-widths:";
const DEFAULT_MIN_COL_WIDTH = 72;

const loadStoredColumnWidths = (storageKey) => {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(COL_WIDTH_STORAGE_PREFIX + storageKey);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return {};
    const widths = {};
    Object.keys(parsed).forEach((key) => {
      const n = Number(parsed[key]);
      if (Number.isFinite(n) && n > 0) widths[key] = Math.round(n);
    });
    return widths;
  } catch (_) {
    return {};
  }
};

const persistColumnWidths = (storageKey, widths) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(COL_WIDTH_STORAGE_PREFIX + storageKey, JSON.stringify(widths));
  } catch (_) {
    /* ignore quota / private mode */
  }
};

const getColumnMinWidth = (col) => {
  if (typeof col?.minWidth === "number" && col.minWidth > 0) return col.minWidth;
  const className = col?.className || "";
  if (className.includes("ib-clients-col-cb")) return 36;
  if (className.includes("action-column")) return 56;
  return DEFAULT_MIN_COL_WIDTH;
};

const buildSizedStyle = (baseStyle, width) => {
  if (!width) return baseStyle;
  return {
    ...(baseStyle || {}),
    width,
    minWidth: width,
    maxWidth: width,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: width,
    boxSizing: "border-box",
    overflow: "hidden",
  };
};

/**
 * DataTable – reusable table component that shares the same CSS classes
 * defined in List.css (accounts-table-container, table-header-row, etc.).
 *
 * Props
 * ─────
 * title            {string}   Table title text
 * subtitle         {string}   Subtitle text (e.g. "(3/10)")
 * columns          {Array}    Column definitions – see shape below
 * data             {Array}    Row objects
 * rowKey           {Function} (row, index) => unique key
 * currentPage      {number}   Current page number (1-based)
 * totalItems       {number}   Total number of items across all pages
 * perPage          {number}   Items per page
 * onPageChange     {Function} (page) => void
 * searchTerm       {string}   Controlled search term
 * onSearchTermChange {Function} (term) => void
 * onSearch         {Function} () => void   – called on Enter / search-btn click
 * onClearSearch    {Function} () => void   – called on clear button / toggle-close
 * searchPlaceholder {string}  Placeholder for the search input
 * headerActions    {ReactNode} Extra action elements shown when search is closed
 * loading          {boolean}  When true, display skeleton rows instead of data
 * loadingMessage   {string}   Accessible loading label (default "Loading…")
 * error            {string}   When set, display an error row instead of data
 * noDataMessage    {string}   Message displayed when data is empty
 * containerClassName {string} Extra className appended to .accounts-table-container
 * tableClassName   {string}   Extra className appended to .accounts-table
 * hideTopToolbar   {boolean} When true, hides title + search header row
 * paginationLeading {ReactNode} Optional content on the left side of the pagination bar
 * skeletonRows     {number}   Number of shimmer rows while loading (default: perPage or 5)
 * compactPagination {boolean} When true, show only the current page between the arrows
 * resizableColumns {boolean} Excel-style drag-to-resize on header borders
 * resizableStorageKey {string} Persist resized widths in localStorage
 *
 * Column shape
 * ────────────
 * {
 *   key:         string,                   – unique identifier
 *   label:       string,                   – header text
 *   className?:  string,                   – CSS class added to header & body cells
 *                                            (use for named columns like "agent-column",
 *                                             "trading-group-column", "action-column"
 *                                             so their width is determined by class, not nth-child)
 *   visible?:    boolean,                  – set to false to hide the column (default true)
 *   render?:     (row, rowIndex) => node,  – custom cell renderer
 *   headerStyle?: object,                  – optional inline style for the header cell
 *   cellStyle?:   object,                  – optional inline style for each body cell
 *   minWidth?:    number,                  – minimum px width when resizableColumns is on
 * }
 *
 * When a column does NOT have a `render` function the component falls back to
 * `row[col.key]`.  If that value is null / undefined it displays "–".
 *
 * Column widths
 * ─────────────
 * By default the existing nth-child CSS rules in List.css determine each
 * column's width based on its position (1st → 76 px, 2nd → 101 px, …).
 * Columns that carry a `className` (e.g. "agent-column") get their width from
 * that class selector instead, which means hiding / showing them will NOT shift
 * the widths of the surrounding positional columns.
 *
 * If the table is used with a column count that differs from the default
 * accounts table layout, pass `headerStyle` / `cellStyle` on each column to
 * override the nth-child sizing as needed.
 */
const DataTable = ({
  title,
  subtitle,
  columns,
  data,
  rowKey,
  currentPage,
  totalItems,
  perPage,
  onPageChange,
  searchTerm,
  onSearchTermChange,
  onSearch,
  onClearSearch,
  searchPlaceholder,
  headerActions,
  loading,
  loadingMessage,
  error,
  noDataMessage,
  containerClassName,
  tableClassName,
  hideTopToolbar = false,
  paginationLeading = null,
  skeletonRows,
  compactPagination = false,
  resizableColumns = false,
  resizableStorageKey = null,
}) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() =>
    resizableColumns ? loadStoredColumnWidths(resizableStorageKey) : {}
  );
  const [resizingKey, setResizingKey] = useState(null);
  const searchContainerRef = useRef(null);
  const tableRef = useRef(null);
  const headerCellRefs = useRef({});
  const liveWidthsRef = useRef({ ...columnWidths });
  const resizeSessionRef = useRef(null);

  // Only render columns whose `visible` prop is not explicitly false
  const visibleColumns = (columns || []).filter(
    (col) => col.visible !== false
  );
  const skeletonRowCount = Math.max(
    1,
    Number(skeletonRows) || Number(perPage) || 5
  );
  const visibleColumnKeys = visibleColumns.map((col) => col.key).join("|");

  const getLiveWidth = (key) =>
    liveWidthsRef.current[key] ?? columnWidths[key];

  const applyLiveColumnWidth = useCallback((colKey, width) => {
    const table = tableRef.current;
    if (!table) return;
    table.querySelectorAll(`[data-col-key="${colKey}"]`).forEach((el) => {
      el.style.width = `${width}px`;
      el.style.minWidth = `${width}px`;
      el.style.maxWidth = `${width}px`;
      el.style.flexGrow = "0";
      el.style.flexShrink = "0";
      el.style.flexBasis = `${width}px`;
      el.style.boxSizing = "border-box";
      el.style.overflow = "hidden";
    });
  }, []);

  useLayoutEffect(() => {
    if (!resizableColumns) return;
    const next = { ...liveWidthsRef.current };
    let changed = false;
    visibleColumns.forEach((col) => {
      if (next[col.key] != null) return;
      const el = headerCellRefs.current[col.key];
      if (!el) return;
      const measured = Math.round(el.getBoundingClientRect().width);
      if (measured > 0) {
        next[col.key] = Math.max(getColumnMinWidth(col), measured);
        changed = true;
      }
    });
    if (!changed) return;
    liveWidthsRef.current = next;
    setColumnWidths(next);
    // visibleColumnKeys tracks column add/remove without resetting stored widths
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizableColumns, visibleColumnKeys]);

  useEffect(() => {
    liveWidthsRef.current = { ...liveWidthsRef.current, ...columnWidths };
  }, [columnWidths]);

  const handleResizePointerDown = useCallback(
    (event, col) => {
      if (!resizableColumns) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      const headerEl = headerCellRefs.current[col.key];
      const startWidth =
        liveWidthsRef.current[col.key] ||
        columnWidths[col.key] ||
        Math.round(headerEl?.getBoundingClientRect().width || getColumnMinWidth(col));
      const isRtl =
        document.documentElement.getAttribute("dir") === "rtl" ||
        document.body?.getAttribute("dir") === "rtl" ||
        (headerEl && getComputedStyle(headerEl).direction === "rtl");

      resizeSessionRef.current = {
        key: col.key,
        startX: event.clientX,
        startWidth,
        minWidth: getColumnMinWidth(col),
        isRtl,
      };
      liveWidthsRef.current[col.key] = startWidth;
      setResizingKey(col.key);

      const onMove = (moveEvent) => {
        const session = resizeSessionRef.current;
        if (!session || session.key !== col.key) return;
        moveEvent.preventDefault();
        const delta = session.isRtl
          ? session.startX - moveEvent.clientX
          : moveEvent.clientX - session.startX;
        const nextWidth = Math.max(
          session.minWidth,
          Math.round(session.startWidth + delta)
        );
        liveWidthsRef.current[col.key] = nextWidth;
        applyLiveColumnWidth(col.key, nextWidth);
      };

      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      const onUp = () => {
        cleanup();
        const session = resizeSessionRef.current;
        const finalWidth =
          liveWidthsRef.current[col.key] ?? session?.startWidth ?? startWidth;
        resizeSessionRef.current = null;
        setResizingKey(null);
        setColumnWidths((prev) => {
          const next = { ...prev, [col.key]: finalWidth };
          persistColumnWidths(resizableStorageKey, next);
          return next;
        });
      };

      resizeSessionRef.current.cleanup = cleanup;
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [resizableColumns, resizableStorageKey, applyLiveColumnWidth, columnWidths]
  );

  useEffect(() => {
    return () => {
      resizeSessionRef.current?.cleanup?.();
    };
  }, []);

  /* ───── Click-outside handler for search ───── */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSearchActive &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        const searchIconBtn = event.target.closest(".action-icon-btn");
        if (!searchIconBtn) {
          setIsSearchActive(false);
        }
      }
    };

    if (isSearchActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchActive, onClearSearch]);

  /* ───── Search helpers ───── */
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && onSearch) {
      onSearch();
    }
  };

  const handleToggleSearch = () => {
    if (isSearchActive) {
      setIsSearchActive(false);
      if (onClearSearch) onClearSearch();
    } else {
      setIsSearchActive(true);
    }
  };

  const handleClearSearchClick = () => {
    setIsSearchActive(false);
    if (onClearSearch) onClearSearch();
  };

  /* ───── Pagination ───── */
  const totalPages =
    Math.ceil((totalItems || (data ? data.length : 0)) / perPage) || 1;

  const renderPaginationButtons = () => {
    if (compactPagination) {
      return (
        <button
          key={currentPage}
          type="button"
          className="pagination-btn active"
          aria-current="page"
        >
          <span>{currentPage}</span>
        </button>
      );
    }

    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage <= 3) {
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages.map((page, index) => {
      if (page === "ellipsis") {
        return (
          <span key={`ellipsis-${index}`} className="pagination-ellipsis">
            ...
          </span>
        );
      }
      return (
        <button
          key={page}
          className={`pagination-btn ${currentPage === page ? "active" : ""}`}
          onClick={() => onPageChange(page)}
        >
          <span>{page}</span>
        </button>
      );
    });
  };

  /* ───── Render ───── */
  return (
    <div className={`accounts-table-container${containerClassName ? ` ${containerClassName}` : ""}`}>
      {/* ── Header ── */}
      {!hideTopToolbar && (
        <div className="table-top-header">
          <div className="accounts-list-title-wrapper">
            <h1 className="accounts-list-title">{title}</h1>
            {subtitle && (
              <span className="accounts-list-subtitle">{subtitle}</span>
            )}
          </div>

          <div className="accounts-list-actions">
            {isSearchActive ? (
              <div className="search-container" ref={searchContainerRef}>
                <input
                  type="text"
                  className="search-input"
                  placeholder={searchPlaceholder || "Search..."}
                  value={searchTerm || ""}
                  onChange={(e) => {
                    if (onSearchTermChange) onSearchTermChange(e.target.value);
                  }}
                  onKeyPress={handleSearchKeyPress}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    className="search-clear-btn"
                    onClick={handleClearSearchClick}
                    aria-label="Clear search"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
                <button
                  className="search-btn"
                  onClick={() => {
                    if (onSearch) onSearch();
                  }}
                  aria-label="Search"
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </div>
            ) : (
              <>
                <button
                  className="action-icon-btn"
                  onClick={handleToggleSearch}
                  aria-label="Search"
                >
                  <i className="fa-solid fa-search"></i>
                </button>
                {headerActions}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Table body: Bootstrap table-responsive + List scroll (matches Accounts/List) ── */}
      <div className="table-responsive accounts-table-scroll w-100">
        <div
          ref={tableRef}
          className={`accounts-table${
            tableClassName ? ` ${tableClassName}` : ""
          }${resizableColumns ? " datatable-resizable" : ""}${
            resizingKey ? " is-col-resizing" : ""
          }`}
        >
          {/* Header row */}
          <div className="table-header-row">
            {visibleColumns.map((col) => (
              <div
                key={col.key}
                ref={(el) => {
                  headerCellRefs.current[col.key] = el;
                }}
                data-col-key={col.key}
                className={`table-header-cell${
                  col.className ? ` ${col.className}` : ""
                }`}
                style={
                  resizableColumns
                    ? buildSizedStyle(col.headerStyle, getLiveWidth(col.key))
                    : col.headerStyle
                }
              >
                {resizableColumns ? (
                  <>
                    <span className="datatable-header-label">{col.label}</span>
                    {col.resizable !== false ? (
                      <span
                        className={`datatable-col-resizer${
                          resizingKey === col.key ? " is-resizing" : ""
                        }`}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${col.label}`}
                        onPointerDown={(event) => handleResizePointerDown(event, col)}
                      />
                    ) : null}
                  </>
                ) : (
                  col.label
                )}
              </div>
            ))}
          </div>

          {/* Data rows */}
          {loading ? (
            Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
              <div
                className="table-row"
                key={`datatable-skeleton-row-${rowIndex}`}
                aria-hidden={rowIndex > 0}
                aria-label={rowIndex === 0 ? loadingMessage || "Loading…" : undefined}
              >
                {visibleColumns.map((col) => (
                  <div
                    key={`datatable-skeleton-${rowIndex}-${col.key}`}
                    className={`table-cell${
                      col.className ? ` ${col.className}` : ""
                    }`}
                    style={col.cellStyle}
                  >
                    <span className="accounts-skeleton-bar accounts-skeleton-bar--cell" />
                  </div>
                ))}
              </div>
            ))
          ) : error ? (
            <div className="table-row no-data-row">
              <div className="table-cell no-data" style={{ width: "100%" }}>
                {error}
              </div>
            </div>
          ) : data && data.length > 0 ? (
            data.map((row, rowIndex) => (
              <div
                key={rowKey ? rowKey(row, rowIndex) : rowIndex}
                className="table-row"
              >
                {visibleColumns.map((col) => (
                  <div
                    key={col.key}
                    data-col-key={col.key}
                    className={`table-cell${
                      col.className ? ` ${col.className}` : ""
                    }`}
                    style={
                      resizableColumns
                        ? buildSizedStyle(col.cellStyle, getLiveWidth(col.key))
                        : col.cellStyle
                    }
                  >
                    {col.render
                      ? col.render(row, rowIndex)
                      : row[col.key] != null
                      ? String(row[col.key])
                      : "-"}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="table-row no-data-row">
              <div className="table-cell no-data" style={{ width: "100%" }}>
                {noDataMessage || "No data found"}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Pagination ── */}
      <div
        className={`accounts-pagination${
          paginationLeading ? " accounts-pagination--with-summary" : ""
        }`}
      >
        {paginationLeading || null}
        <div
          className={`pagination-controls${
            compactPagination ? " pagination-controls--current-only" : ""
          }`}
        >
          <button
            className="pagination-btn"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <i className="fa-solid fa-chevron-left"></i>
          </button>
          {renderPaginationButtons()}
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
